import React, { useState } from 'react';
import {
  FiMousePointer,
  FiMinus,
  FiType,
  FiSquare,
  FiUpload,
  FiImage,
  FiTrash2,
  FiLayout,
  FiFolder,
  FiGrid,
  FiBarChart2,
  FiCommand,
  FiChevronLeft,
  FiChevronRight,
} from 'react-icons/fi';
import useEditorStore from '../../stores/useEditorStore';
import { SIDEBAR_TOGGLE } from '../../constants/editorConstants';
import ProjectSettingsPanel from './panels/ProjectSettingsPanel';
import SelectSettingsPanel from './panels/SelectSettingsPanel';
import LineSettingsPanel from './panels/LineSettingsPanel';
import TextSettingsPanel from './panels/TextSettingsPanel';
import ShapeSettingsPanel from './panels/ShapeSettingsPanel';
import ImageSettingsPanel from './panels/ImageSettingsPanel';
import TemplateSettingsPanel from './panels/TemplateSettingsPanel';
import UploadSettingsPanel from './panels/UploadSettingsPanel';
import TableSettingsPanel from './panels/TableSettingsPanel';
import ChartSettingsPanel from './panels/ChartSettingsPanel';
import ShortcutModal from '../common/ShortcutModal';

const Toolbar = () => {
  const { canvas, activeTool, setActiveTool, pushHistory } = useEditorStore();
  const [isPanelOpen, setIsPanelOpen] = useState(true);
  const [showShortcutModal, setShowShortcutModal] = useState(false);

  // 순서: 프로젝트 > 선택 > 라인 > 텍스트 > 도형 > 이미지 > 템플릿 > 업로드
  const tools = [
    { id: 'project', icon: FiFolder, label: '프로젝트' },
    { id: 'select', icon: FiMousePointer, label: '선택' },
    { id: 'line', icon: FiMinus, label: '라인' },
    { id: 'text', icon: FiType, label: '텍스트' },
    { id: 'shape', icon: FiSquare, label: '도형' },
    { id: 'table', icon: FiGrid, label: '표' },
    { id: 'chart', icon: FiBarChart2, label: '차트' },
    { id: 'image', icon: FiImage, label: '이미지' },
    { id: 'template', icon: FiLayout, label: '템플릿' },
    { id: 'upload', icon: FiUpload, label: '업로드' },
  ];

  const handleToolSelect = (toolId) => {
    setActiveTool(toolId);
    setIsPanelOpen(true);
  };

  const handleDelete = () => {
    if (!canvas) return;
    const activeObjects = canvas.getActiveObjects();
    if (activeObjects.length > 0) {
      activeObjects.forEach(obj => canvas.remove(obj));
      canvas.discardActiveObject();
      canvas.renderAll();
      pushHistory();
    }
  };

  const renderPanel = () => {
    switch (activeTool) {
      case 'project': return <ProjectSettingsPanel />;
      case 'select': return <SelectSettingsPanel />;
      case 'line': return <LineSettingsPanel />;
      case 'text': return <TextSettingsPanel />;
      case 'shape': return <ShapeSettingsPanel />;
      case 'table': return <TableSettingsPanel />;
      case 'chart': return <ChartSettingsPanel />;
      case 'image': return <ImageSettingsPanel />;
      case 'template': return <TemplateSettingsPanel />;
      case 'upload': return <UploadSettingsPanel />;
      default: return <SelectSettingsPanel />;
    }
  };

  return (
    <div style={{ display: 'flex', height: '100%', position: 'relative' }}>
      {/* 아이콘 바 */}
      <div
        style={{
          width: '72px',
          backgroundColor: '#ffffff',
          borderRight: '1px solid #e5e7eb',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '12px 0',
          gap: '4px',
          flexShrink: 0,
        }}
      >
        {tools.map((tool) => (
          <button
            key={tool.id}
            onClick={() => handleToolSelect(tool.id)}
            style={{
              width: '60px',
              padding: '10px 4px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '4px',
              border: 'none',
              borderRadius: '8px',
              backgroundColor: activeTool === tool.id ? '#eff6ff' : 'transparent',
              color: activeTool === tool.id ? '#3b82f6' : '#6b7280',
              cursor: 'pointer',
              transition: 'all 0.15s',
              borderLeft: activeTool === tool.id ? '3px solid #3b82f6' : '3px solid transparent',
            }}
            title={tool.label}
          >
            <tool.icon size={20} />
            <span style={{ fontSize: '10px', fontWeight: '500' }}>{tool.label}</span>
          </button>
        ))}

        {/* 하단 고정 - 단축키 + 삭제 버튼 */}
        <div style={{ marginTop: 'auto', paddingTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <button
            onClick={() => setShowShortcutModal(true)}
            style={{
              width: '60px', padding: '10px 4px',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
              border: 'none', borderRadius: '8px', backgroundColor: 'transparent',
              color: '#6b7280', cursor: 'pointer', transition: 'all 0.15s',
            }}
            title="키보드 단축키"
          >
            <FiCommand size={20} />
            <span style={{ fontSize: '10px', fontWeight: '500' }}>단축키</span>
          </button>
          <button
            onClick={handleDelete}
            style={{
              width: '60px', padding: '10px 4px',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
              border: 'none', borderRadius: '8px', backgroundColor: 'transparent',
              color: '#dc2626', cursor: 'pointer', transition: 'all 0.15s',
            }}
            title="삭제 (Delete)"
          >
            <FiTrash2 size={20} />
            <span style={{ fontSize: '10px', fontWeight: '500' }}>삭제</span>
          </button>
        </div>
      </div>

      {/* 단축키 모달 */}
      {showShortcutModal && <ShortcutModal onClose={() => setShowShortcutModal(false)} />}

      {/* 설정 패널 */}
      <div
        style={{
          width: isPanelOpen ? '280px' : '0px',
          backgroundColor: '#ffffff',
          borderRight: isPanelOpen ? '1px solid #e5e7eb' : 'none',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 0.25s ease',
        }}
      >
        <div style={{ width: '280px', height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {renderPanel()}
        </div>
      </div>

      {/* 패널 접기/펼치기 버튼 */}
      <button
        onClick={() => setIsPanelOpen(!isPanelOpen)}
        style={{
          position: 'absolute',
          right: `-${SIDEBAR_TOGGLE.WIDTH}px`,
          top: '50%',
          transform: 'translateY(-50%)',
          width: `${SIDEBAR_TOGGLE.WIDTH}px`,
          height: `${SIDEBAR_TOGGLE.HEIGHT}px`,
          backgroundColor: SIDEBAR_TOGGLE.BG,
          border: '1px solid #e5e7eb',
          borderLeft: 'none',
          borderRadius: '0 6px 6px 0',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
          zIndex: 10,
          color: SIDEBAR_TOGGLE.COLOR,
          transition: 'color 0.15s, background-color 0.15s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = SIDEBAR_TOGGLE.HOVER_BG; e.currentTarget.style.color = SIDEBAR_TOGGLE.HOVER_COLOR; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = SIDEBAR_TOGGLE.BG; e.currentTarget.style.color = SIDEBAR_TOGGLE.COLOR; }}
        title={isPanelOpen ? '패널 접기' : '패널 펼치기'}
      >
        {isPanelOpen ? <FiChevronLeft size={SIDEBAR_TOGGLE.ICON_SIZE} strokeWidth={SIDEBAR_TOGGLE.ICON_STROKE_WIDTH} /> : <FiChevronRight size={SIDEBAR_TOGGLE.ICON_SIZE} strokeWidth={SIDEBAR_TOGGLE.ICON_STROKE_WIDTH} />}
      </button>
    </div>
  );
};

export default Toolbar;
